import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import { format as formatDate } from "date-fns"
import { capitalize, isNumber } from "lodash"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import Row from "../../../common/components/Row"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import {
  HeaderTableCell,
  NoWrapTableCell,
  NumberTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../common/components/tables/cells"
import {
  formatTableDate,
  getTableSearchParams,
  useTableSearchTextState,
  useTableSort,
} from "../../../common/components/tables/utils"
import BorderedGrayChip from "../components/BorderedGrayChip"
import SettingsSectionCard from "./SettingsSectionCard"

function formatInvoiceDate(row) {
  const dateStr = row.payment_date || row.due_date || row.created_at
  if (dateStr == null || dateStr === "") {
    return null
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) {
      return null
    }
    return formatDate(date, "MMM d yyyy")
  }
  return formatTableDate(dateStr)
}

function getInvoiceDownloadUrl(row) {
  return row.pdf_file || row.pdf_url || row.invoice_url || null
}

export default function InvoicesTable() {
  const [tableRows, setTableRows] = useState([])
  const [searchQuery, setSearchQuery, debouncedSearchQuery] =
    useTableSearchTextState("iq")

  const { serverSort, onSortColumnChanged, sortColumn, sortDirection } =
    useTableSort("payment_date", "desc")
  const [isTableLoading, setIsTableLoading] = useState(false)
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      setIsTableLoading(true)
      axios
        .get("/billing/api/invoices/", {
          params: getTableSearchParams(
            debouncedSearchQuery,
            null,
            null,
            serverSort
          ),
        })
        .then(({ data }) => {
          setTableRows(data)
        })
        .finally(() => setIsTableLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchQuery, serverSort]
  )

  return (
    <SettingsSectionCard>
      <Row
        sx={{
          flexDirection: "row-reverse",
        }}
      >
        <TextField
          size="small"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: "20em" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Row>
      <Column sx={{ mt: 3 }}>
        <ConditionalPurpleLinearProgress visible={isTableLoading} />
        <ScrollableTableContainer sx={{ maxHeight: "calc(100vh - 420px)" }}>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  HeaderCellComponent={StickyHeaderTableCell}
                  label="Payment date"
                  columnName="payment_date"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  label="Amount"
                  columnName="amount"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  label="Status"
                  columnName="status"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <HeaderTableCell>Download</HeaderTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isTableLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <TableRow key={index}>
                    <StickyTableCell>
                      <Skeleton variant="rounded" height={30} />
                    </StickyTableCell>
                    <NumberTableCell>
                      <Skeleton variant="rounded" height={30} />
                    </NumberTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="rounded" height={30} width={100} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="circular" height={32} width={32} />
                    </NoWrapTableCell>
                  </TableRow>
                ))
              ) : tableRows.length > 0 ? (
                tableRows.map((row) => (
                  <TableRow key={row.id}>
                    <StickyTableCell>{formatInvoiceDate(row)}</StickyTableCell>
                    <NumberTableCell>
                      {isNumber(row.amount) ? `$${row.amount}` : undefined}
                    </NumberTableCell>
                    <NoWrapTableCell>
                      {row.status ? (
                        <BorderedGrayChip label={capitalize(row.status)} />
                      ) : undefined}
                    </NoWrapTableCell>
                    <NoWrapTableCell sx={{ px: 0, textAlign: "center" }}>
                      {getInvoiceDownloadUrl(row) ? (
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() =>
                            window
                              .open(getInvoiceDownloadUrl(row), "_blank")
                              .focus()
                          }
                        >
                          <FileDownloadOutlinedIcon />
                        </IconButton>
                      ) : null}
                    </NoWrapTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <NoWrapTableCell colSpan={5} sx={{ textAlign: "center" }}>
                    No invoices to show here
                  </NoWrapTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
      </Column>
    </SettingsSectionCard>
  )
}
